/**
 * OpenCodeHeadlessHarness — drives `opencode run --format json`, a real
 * non-interactive mode with a structured NDJSON event stream.
 *
 * This replaces the raw-relay PTY path for CARD work. The PTY path remains
 * correct for adhoc sessions, where a human drives the terminal and raw relay is
 * the point; a card wants events, tools and a turn that ends.
 *
 * What headless buys over raw relay (all verified against opencode 1.18.15):
 *   - structured events: text + tool calls, so chat mirroring and activity
 *     summaries work instead of a terminal nobody can parse,
 *   - the Conveyor MCP tools, served by the SAME in-process StreamableHTTP tool
 *     server the Claude path uses (`opencode mcp list` reports it connected, and
 *     a run called `conveyor_conveyor_ping` and got its output back),
 *   - resume, via the session id opencode reports on every event,
 *   - a turn that actually ends — the process exits when the run completes, so
 *     no prompt-delivery probing, no bracketed paste, and no wedge watchdog.
 *
 * Two operational details are load-bearing:
 *   - STDIN MUST BE CLOSED. `opencode run` with an open stdin pipe blocks
 *     forever, emitting nothing on stdout or stderr — no error, no timeout.
 *     Verified: a 5-minute hang with zero bytes. `stdio: "ignore"` for stdin.
 *   - MCP config rides `OPENCODE_CONFIG_CONTENT` (inline JSON, high precedence).
 *     The alternative — `opencode.json` in the workspace root — would dirty the
 *     checked-out repo, and the global `~/.config/opencode` would leak between
 *     concurrent sessions.
 */

import { spawn } from "node:child_process";
import type {
  AgentHarness,
  HarnessEvent,
  HarnessMcpServer,
  HarnessQueryOptions,
  HarnessToolDefinition,
} from "../types.js";
import { PtyMcpServer } from "../pty/mcp-server.js";
import { startToolServers, type PtyToolServer } from "../pty/tool-server.js";
import { AsyncEventQueue } from "../pty/event-queue.js";
import { sessionTempBase } from "../pty/pty-support.js";
import {
  buildOpenCodeConfigContent,
  buildOpenCodeChildEnv,
  buildRunArgs,
  prepareOpenCodeCredentials,
  resolveOpenCodeBinary,
  resolveOpenCodeModel,
} from "./spawn.js";
import {
  accumulateUsage,
  buildResultEvent,
  errorMessageOf,
  mapOpenCodeEvent,
  parseOpenCodeLine,
  sessionIdOf,
  type OpenCodeUsage,
} from "./events.js";
import { mkdtemp, rm, writeFile } from "node:fs/promises";
import { join } from "node:path";

/** Bounded stderr tail kept to explain a non-zero exit. */
const MAX_STDERR_TAIL = 4000;

export class OpenCodeHeadlessHarness implements AgentHarness {
  /** NDJSON from `--format json` is a trusted structured source. */
  readonly emitsStructuredEvents = true;

  /** opencode's session id from the last run, for `--session` on the next turn. */
  private lastSessionId: string | null = null;

  /** MCP tool servers started for the in-flight run. */
  private toolServers: PtyToolServer[] = [];
  private tempDir = "";

  /** The same in-process MCP handle the PTY path uses — tools are identical. */
  createMcpServer(config: { name: string; tools: HarnessToolDefinition[] }): HarnessMcpServer {
    return new PtyMcpServer(config.name, config.tools);
  }

  /** opencode's own session id, exposed so the runner can persist lineage. */
  get sessionId(): string | null {
    return this.lastSessionId;
  }

  async *executeQuery(opts: {
    prompt: string | AsyncGenerator<never, void, unknown>;
    options: HarnessQueryOptions;
    resume?: string;
  }): AsyncGenerator<HarnessEvent, void> {
    const prompt = await collectPrompt(opts.prompt);
    const binary = resolveOpenCodeBinary(process.env);
    // A deployed pod authenticates one of two ways; both are prepared here (see
    // ./credentials.ts). Without this the child inherits Conveyor's own
    // CONVEYOR_* variables, which opencode does not read — i.e. no credential.
    await prepareOpenCodeCredentials(process.env);

    this.tempDir = await mkdtemp(join(sessionTempBase(), "opencode-headless-"));
    const { servers, entries } = await startToolServers(
      opts.options.mcpServers ?? {},
      this.tempDir,
    );
    this.toolServers = servers;

    const args = buildRunArgs({
      prompt,
      cwd: opts.options.cwd,
      model: resolveOpenCodeModel(process.env, opts.options.model),
      // `resume` is Claude-transcript-derived, so it is always absent here (there
      // is no Claude session file for an opencode run). Falling back to the id
      // opencode itself reported is what makes a multi-turn card keep its
      // conversation instead of starting fresh on every message.
      resumeSessionId: opts.resume ?? this.lastSessionId,
    });
    // Read-only modes arrive as Claude's permissionMode "plan" — the flag we can
    // rely on is that the runner withholds the skip-permissions grant.
    const readOnly = opts.options.allowDangerouslySkipPermissions === false;
    const systemPromptPath = await this.writeSystemPrompt(opts.options.appendSystemPrompt);
    const configContent = buildOpenCodeConfigContent({
      entries,
      systemPromptPath,
      readOnly,
    });

    const queue = new AsyncEventQueue<HarnessEvent>();
    const usage: OpenCodeUsage = { inputTokens: 0, outputTokens: 0, totalCostUsd: 0 };
    let assistantText = "";
    let stderrTail = "";
    // opencode reports provider failures (401s especially) as a stdout event, so
    // this is what makes an auth failure diagnosable on the card.
    let reportedError: string | null = null;

    const child = spawn(binary, args, {
      cwd: opts.options.cwd,
      // stdin IGNORED — an open pipe makes `opencode run` block forever.
      stdio: ["ignore", "pipe", "pipe"],
      env: buildOpenCodeChildEnv(process.env, configContent),
    });

    const abort = opts.options.abortController;
    const onAbort = (): void => {
      child.kill("SIGTERM");
    };
    abort?.signal.addEventListener("abort", onAbort, { once: true });

    // NDJSON is line-delimited but arrives in arbitrary chunks.
    let carry = "";
    const ingest = (line: string): void => {
      const { text, error } = this.ingestLine(line, usage, queue);
      if (text) assistantText += text;
      if (error) reportedError = error;
    };
    child.stdout.setEncoding("utf8");
    child.stdout.on("data", (chunk: string) => {
      carry += chunk;
      const lines = carry.split("\n");
      carry = lines.pop() ?? "";
      for (const line of lines) ingest(line);
    });

    child.stderr.setEncoding("utf8");
    child.stderr.on("data", (chunk: string) => {
      stderrTail = (stderrTail + chunk).slice(-MAX_STDERR_TAIL);
    });

    const exited = new Promise<number>((resolve) => {
      child.once("error", () => resolve(-1));
      child.once("close", (code) => resolve(code ?? -1));
    });

    const finish = (async (): Promise<void> => {
      const code = await exited;
      // A trailing line without its newline still carries an event.
      ingest(carry);
      queue.push(buildResultEvent(code, usage, assistantText.trim(), stderrTail, reportedError));
      queue.close();
    })();

    try {
      for await (const event of queue.drain()) yield event;
      await finish;
    } finally {
      abort?.signal.removeEventListener("abort", onAbort);
      if (!child.killed) child.kill("SIGTERM");
      await this.cleanup();
    }
  }

  /**
   * Persist the Conveyor system prompt (task context, plan, mode instructions)
   * so it can be referenced by the config's `instructions`. opencode has no
   * `--append-system-prompt`; without this the card runs with no context at all.
   */
  private async writeSystemPrompt(text: string | undefined): Promise<string | null> {
    if (!text || text.trim() === "") return null;
    const path = join(this.tempDir, "conveyor-instructions.md");
    await writeFile(path, text, "utf8");
    return path;
  }

  /**
   * Parse one NDJSON line and push whatever it maps to. Returns assistant text so
   * the caller can accumulate the turn's summary; the session id is latched here
   * because every event carries it and any one of them will do.
   */
  private ingestLine(
    line: string,
    usage: OpenCodeUsage,
    queue: AsyncEventQueue<HarnessEvent>,
  ): { text: string; error: string | null } {
    const none = { text: "", error: null };
    const parsed = parseOpenCodeLine(line);
    if (!parsed) return none;
    const sid = sessionIdOf(parsed);
    if (sid) this.lastSessionId = sid;
    accumulateUsage(parsed, usage);
    const error = errorMessageOf(parsed);
    if (error) return { text: "", error };
    const mapped = mapOpenCodeEvent(parsed);
    if (!mapped) return none;
    queue.push(mapped);
    if (mapped.type !== "assistant") return none;
    const block = mapped.message.content[0];
    return { text: block?.type === "text" && block.text ? block.text : "", error: null };
  }

  async dispose(): Promise<void> {
    await this.cleanup();
  }

  private async cleanup(): Promise<void> {
    for (const server of this.toolServers) await server.close().catch(() => undefined);
    this.toolServers = [];
    if (this.tempDir) {
      await rm(this.tempDir, { recursive: true, force: true }).catch(() => undefined);
      this.tempDir = "";
    }
  }
}

/** The runner may pass a streaming prompt; headless takes one string. */
async function collectPrompt(
  prompt: string | AsyncGenerator<never, void, unknown>,
): Promise<string> {
  if (typeof prompt === "string") return prompt;
  const parts: string[] = [];
  for await (const message of prompt as AsyncGenerator<
    { message?: { content?: unknown } },
    void,
    unknown
  >) {
    const content = message?.message?.content;
    if (typeof content === "string") parts.push(content);
    else if (Array.isArray(content)) {
      for (const block of content) {
        const b = block as { type?: string; text?: string };
        if (b?.type === "text" && typeof b.text === "string") parts.push(b.text);
      }
    }
  }
  return parts.join("\n\n");
}
